// Reusable decorative wave (`<fv-wave>`).
//
// The element ships with a static wave path baked into its SVG. When
// `data-interactive="true"` (and the user hasn't requested reduced motion) it
// upgrades to an interactive wave that gently ripples on its own and pushes a
// crest toward the pointer as it moves across the host section.
//
// On coarse/mobile pointers we keep the static path — perpetual path morphing
// fights scroll paint. When interactive, the rAF loop pauses off-screen.

const VIEW_W = 1440;
const VIEW_H = 150;
const SAMPLES = 40;
const BASELINE = 80; // resting height of the wave trough line
const IDLE_AMP_1 = 28;
const IDLE_AMP_2 = 14;
const MOUSE_AMP = 55; // how far the crest dips toward the cursor
const MOUSE_SIGMA = 0.13; // width of the cursor influence (0..1 of section width)
const EASE = 0.08;

function buildPath(points) {
  // Smooth the sampled top points with a Catmull-Rom -> cubic bezier conversion,
  // then close the shape across the top edge so the fill sits above the wave.
  let d = `M0,0 L${VIEW_W},0 L${VIEW_W},${points[points.length - 1].y.toFixed(2)}`;

  for (let i = points.length - 1; i > 0; i--) {
    const p0 = points[Math.min(i + 1, points.length - 1)];
    const p1 = points[i];
    const p2 = points[i - 1];
    const p3 = points[Math.max(i - 2, 0)];

    const c1x = p1.x + (p2.x - p0.x) / 6;
    const c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6;
    const c2y = p2.y - (p3.y - p1.y) / 6;

    d += ` C${c1x.toFixed(2)},${c1y.toFixed(2)} ${c2x.toFixed(2)},${c2y.toFixed(2)} ${p2.x.toFixed(2)},${p2.y.toFixed(2)}`;
  }

  d += ' Z';
  return d;
}

class FvWave extends HTMLElement {
  connectedCallback() {
    this.path = this.querySelector('path');
    if (!this.path) return;

    this.interactive = this.dataset.interactive === 'true';
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.coarse = window.matchMedia('(pointer: coarse), (max-width: 749px)').matches;

    // Non-interactive, reduced motion, or mobile: keep the authored static wave.
    if (!this.interactive || this.reduced || this.coarse) return;

    this.phase = 0;
    this.mouseNX = 0.5; // normalised cursor x (0..1)
    this.influence = 0; // current eased cursor influence (0..1)
    this.targetInfluence = 0;
    this.inView = true;
    this.running = false;

    this.onMove = this.onMove.bind(this);
    this.onLeave = this.onLeave.bind(this);
    this.tick = this.tick.bind(this);
    this.onVisibility = this.onVisibility.bind(this);

    // Track the pointer across the whole band the wave belongs to.
    this.host = this.closest('[data-fv-wave-host]') || this.closest('.shopify-section') || this.parentElement || this;
    this.host.addEventListener('pointermove', this.onMove);
    this.host.addEventListener('pointerleave', this.onLeave);

    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver(
        (entries) => {
          this.inView = entries.some((e) => e.isIntersecting);
          this.syncLoop();
        },
        { rootMargin: '15% 0px' }
      );
      this.io.observe(this);
    }

    document.addEventListener('visibilitychange', this.onVisibility);
    this.syncLoop();
  }

  disconnectedCallback() {
    this.stopLoop();
    if (this.host) {
      this.host.removeEventListener('pointermove', this.onMove);
      this.host.removeEventListener('pointerleave', this.onLeave);
    }
    if (this.io) this.io.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  onVisibility() {
    this.syncLoop();
  }

  syncLoop() {
    const shouldRun = this.inView && !document.hidden;
    if (shouldRun) this.startLoop();
    else this.stopLoop();
  }

  startLoop() {
    if (this.running) return;
    this.running = true;
    this.raf = requestAnimationFrame(this.tick);
  }

  stopLoop() {
    this.running = false;
    if (this.raf) {
      cancelAnimationFrame(this.raf);
      this.raf = 0;
    }
  }

  onMove(event) {
    const rect = this.host.getBoundingClientRect();
    if (rect.width === 0) return;
    this.mouseNX = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    this.targetInfluence = 1;
  }

  onLeave() {
    this.targetInfluence = 0;
  }

  render() {
    const points = [];
    for (let i = 0; i <= SAMPLES; i++) {
      const t = i / SAMPLES;
      const x = t * VIEW_W;

      let y = BASELINE;
      y += Math.sin(t * Math.PI * 3 + this.phase) * IDLE_AMP_1;
      y += Math.sin(t * Math.PI * 5 - this.phase * 0.7) * IDLE_AMP_2;

      const dx = t - this.mouseNX;
      const bump = Math.exp(-(dx * dx) / (2 * MOUSE_SIGMA * MOUSE_SIGMA));
      y += bump * MOUSE_AMP * this.influence;

      if (y > VIEW_H - 2) y = VIEW_H - 2;
      points.push({ x, y });
    }
    this.path.setAttribute('d', buildPath(points));
  }

  tick() {
    if (!this.running) return;
    this.phase += 0.02;
    this.influence += (this.targetInfluence - this.influence) * EASE;
    this.render();
    this.raf = requestAnimationFrame(this.tick);
  }
}

if (!customElements.get('fv-wave')) {
  customElements.define('fv-wave', FvWave);
}
